import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  StarIcon,
  UsersIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  MapPinIcon,
  ChevronDownIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface TalentProfile {
  id: string;
  firstName: string;
  lastName: string;
  username?: string;
  email: string;
  bio?: string;
  skills: string[];
  interests: string[];
  coursesCompleted: number;
  coursesEnrolled: number;
  certificationsEarned: number;
  averageScore: number;
  overallRanking?: number;
  profilePicture?: string;
  location?: string;
  university?: string;
  major?: string;
  graduationYear?: number;
  experience?: any[];
  skillRatings?: { skill: string; rating: number }[];
  portfolioUrl?: string;
  linkedinUrl?: string;
  githubUrl?: string;
  createdAt?: Date;
}

interface TalentFilters {
  search?: string;
  skills?: string[];
  location?: string;
  experienceLevel?: string;
  minScore?: number;
  sortBy?: 'ranking' | 'score' | 'courses' | 'recent';
}

const TalentPage: React.FC = () => {
  const { currentUser, getToken } = useAuth();
  const location = useLocation();
  const [talents, setTalents] = useState<TalentProfile[]>([]);
  const [filteredTalents, setFilteredTalents] = useState<TalentProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<TalentFilters>({});

  // Extract search query from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
      setFilters(prev => ({ ...prev, search: searchParam }));
    }
  }, [location.search]);

  // Fetch talent profiles
  useEffect(() => {
    fetchTalents();
  }, []);

  // Apply filters when search query or filters change
  useEffect(() => {
    applyFilters();
  }, [talents, searchQuery, filters]);

  const fetchTalents = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = currentUser ? await getToken() : null;
      const response = await fetch('/api/jobs/employer/candidates', {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setTalents(result.data);
      } else {
        setError('Failed to load talent profiles');
      }
    } catch (err) {
      console.error('Error fetching talent:', err);
      setError('Unable to load talent profiles. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...talents];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(talent => 
        `${talent.firstName} ${talent.lastName}`.toLowerCase().includes(query) ||
        talent.username?.toLowerCase().includes(query) ||
        talent.bio?.toLowerCase().includes(query) ||
        talent.skills.some(skill => skill.toLowerCase().includes(query)) ||
        talent.interests.some(interest => interest.toLowerCase().includes(query)) ||
        talent.major?.toLowerCase().includes(query) ||
        talent.university?.toLowerCase().includes(query)
      );
    }

    // Skills filter
    if (filters.skills && filters.skills.length > 0) {
      filtered = filtered.filter(talent =>
        filters.skills!.some(filterSkill =>
          talent.skills.some(talentSkill =>
            talentSkill.toLowerCase().includes(filterSkill.toLowerCase())
          )
        )
      );
    }

    // Location filter
    if (filters.location) {
      filtered = filtered.filter(talent =>
        talent.location?.toLowerCase().includes(filters.location!.toLowerCase())
      );
    }

    // Minimum score filter
    if (filters.minScore) {
      filtered = filtered.filter(talent => talent.averageScore >= filters.minScore!);
    }

    // Sorting
    const sortBy = filters.sortBy || 'ranking';
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'ranking':
          return (a.overallRanking || 999999) - (b.overallRanking || 999999);
        case 'score':
          return b.averageScore - a.averageScore;
        case 'courses':
          return b.coursesCompleted - a.coursesCompleted;
        case 'recent':
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB.getTime() - dateA.getTime();
        default:
          return 0;
      }
    });

    setFilteredTalents(filtered);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleFilterChange = (key: keyof TalentFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  const getSkillBadgeColor = (index: number) => {
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-purple-100 text-purple-800',
      'bg-orange-100 text-orange-800',
      'bg-pink-100 text-pink-800'
    ];
    return colors[index % colors.length];
  };

  const getGradeColor = (score: number) => {
    if (score >= 95) return 'bg-emerald-100 text-emerald-800';
    if (score >= 90) return 'bg-green-100 text-green-800';
    if (score >= 85) return 'bg-blue-100 text-blue-800';
    if (score >= 80) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getLetterGrade = (score: number) => {
    if (score >= 97) return 'A+';
    if (score >= 93) return 'A';
    if (score >= 90) return 'A-';
    if (score >= 87) return 'B+';
    if (score >= 83) return 'B';
    if (score >= 80) return 'B-';
    if (score >= 77) return 'C+';
    if (score >= 73) return 'C';
    if (score >= 70) return 'C-';
    return 'F';
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Sign In Required</h2>
          <p className="text-gray-600 mb-4">Please sign in to browse talent profiles and connect with candidates.</p>
          <Link
            to="/login"
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-md p-6">
                  <div className="h-16 w-16 bg-gray-300 rounded-full mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-3/4 mb-4"></div>
                  <div className="space-y-2">
                    <div className="h-6 bg-gray-300 rounded"></div>
                    <div className="h-6 bg-gray-300 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-red-500 mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Talent</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchTalents}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Top Talent</h1>
          <p className="text-gray-600">Discover skilled professionals and connect with top candidates</p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search by name, skills, bio, interests..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <FunnelIcon className="h-5 w-5 mr-2" />
              Filters
              <ChevronDownIcon className={`h-4 w-4 ml-2 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showFilters && (
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={filters.location || ''}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                    placeholder="City, State"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Score</label>
                  <select
                    value={filters.minScore || ''}
                    onChange={(e) => handleFilterChange('minScore', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Any Score</option>
                    <option value="90">90+ (A)</option>
                    <option value="80">80+ (B)</option>
                    <option value="70">70+ (C)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                  <select
                    value={filters.sortBy || 'ranking'}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="ranking">Ranking</option>
                    <option value="score">Highest Score</option>
                    <option value="courses">Most Courses</option>
                    <option value="recent">Most Recent</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={clearFilters}
                    className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="text-sm text-gray-600">
            Showing {filteredTalents.length} of {talents.length} candidates
          </div>
        </div>

        {/* Talent Grid */}
        {filteredTalents.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Candidates Found</h3>
            <p className="text-gray-600">Try adjusting your search criteria or filters to find more candidates.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTalents.map((talent) => (
              <div key={talent.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                {/* Header with avatar and ranking */}
                <div className="bg-gradient-to-r from-primary-50 to-blue-50 px-6 py-4 flex items-center space-x-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                      {talent.profilePicture ? (
                        <img 
                          src={talent.profilePicture} 
                          alt={`${talent.firstName} ${talent.lastName}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-primary-100 text-primary-600 text-lg font-bold rounded-full flex items-center justify-center">
                          {talent.firstName.charAt(0)}{talent.lastName.charAt(0)}
                        </div>
                      )}
                    </div>
                    {talent.overallRanking && talent.overallRanking <= 100 && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full flex items-center justify-center">
                        #{talent.overallRanking}
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      {talent.firstName} {talent.lastName}
                    </h3>
                    {talent.username && (
                      <p className="text-sm text-gray-600">@{talent.username}</p>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center">
                        <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                        <span className="text-sm text-gray-600">{talent.averageScore}%</span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${getGradeColor(talent.averageScore)}`}>
                        {getLetterGrade(talent.averageScore)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Bio */}
                  {talent.bio && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{talent.bio}</p>
                  )}

                  {/* Education & Location */}
                  <div className="mb-4">
                    {talent.university && (
                      <div className="flex items-center text-sm text-gray-600 mb-1">
                        <AcademicCapIcon className="h-4 w-4 mr-1" />
                        {talent.major} at {talent.university}
                        {talent.graduationYear && ` (${talent.graduationYear})`}
                      </div>
                    )}
                    {talent.location && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPinIcon className="h-4 w-4 mr-1" />
                        {talent.location}
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center">
                      <div className="text-lg font-bold text-primary-600">{talent.coursesCompleted}</div>
                      <div className="text-xs text-gray-500">Courses</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">{talent.certificationsEarned}</div>
                      <div className="text-xs text-gray-500">Certs</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-purple-600">
                        {talent.overallRanking ? `#${talent.overallRanking}` : 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500">Rank</div>
                    </div>
                  </div>

                  {/* Skills */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-600 mb-2">Top Skills:</p>
                    <div className="flex flex-wrap gap-1">
                      {talent.skills.slice(0, 4).map((skill, idx) => (
                        <span
                          key={idx}
                          className={`px-2 py-1 text-xs rounded ${getSkillBadgeColor(idx)}`}
                        >
                          {skill}
                        </span>
                      ))}
                      {talent.skills.length > 4 && (
                        <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600">
                          +{talent.skills.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Interests */}
                  {talent.interests && talent.interests.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-600 mb-2">Interests:</p>
                      <div className="flex flex-wrap gap-1">
                        {talent.interests.slice(0, 3).map((interest, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600"
                          >
                            {interest}
                          </span>
                        ))}
                        {talent.interests.length > 3 && (
                          <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600">
                            +{talent.interests.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Link
                      to={`/talent/${talent.id}`}
                      className="flex-1 bg-primary-600 text-white text-center py-2 rounded-md hover:bg-primary-700 transition-colors text-sm font-medium"
                    >
                      View Profile
                    </Link>
                    <Link
                      to={`/messages/new?userId=${talent.id}`}
                      className="flex-1 border border-primary-600 text-primary-600 text-center py-2 rounded-md hover:bg-primary-50 transition-colors text-sm font-medium"
                    >
                      Contact
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TalentPage;